import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1041SchD = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1041 Schedule D Filing Guide for Estates and Trusts"
          description="Form 1041 Schedule D helps report capital gains. Learn about filing requirements, common questions, and how TaxZerone streamlines your e-filing journey."
          keywords=""
          canonical="resources/tax-exempt/schedule-d-form-1041-resource-guide/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 1041{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule D</span>
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/schedule-d-form-1041-resource-guide/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Schedule D (Form 1041) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Schedule D (Form 1041)
                        </strong>
                        is used to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          capital gains and losses{' '}
                        </strong>{' '}
                        to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          capital gains and losses
                        </strong>
                        from the sale or exchange of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          capital assets{' '}
                        </strong>
                        by an estate or trust.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Estates and trusts filing{' '}
                        <strong style={{ fontWeight: 600 }}>Form 1041</strong>{' '}
                        generally use Schedule D when they have{' '}
                        <strong style={{ fontWeight: 600 }}>
                          capital gains or losses
                        </strong>{' '}
                        that must be reported separately from ordinary income.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule D (Form 1041) reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          short-term and long-term capital gains and losses
                        </strong>{' '}
                        including gains or losses from the sale or exchange of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          stocks, securities, real estate,{' '}
                        </strong>
                        and other capital assets.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Related Tax Filing:{' '}
                        </strong>
                        Organizations researching tax reporting for{' '}
                        <strong style={{ fontWeight: 600 }}>
                          unrelated business income{' '}
                        </strong>{' '}
                        may also need to review{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-T
                        </Link>{' '}
                        and its applicable schedules.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule D (Form 1041)?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule D (Form 1041)?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Schedule D (Form 1041) filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is Schedule D (Form 1041)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule D (Form 1041) is a supplemental form that estates and
                  trusts use to detail their capital gains and losses from
                  transactions involving capital assets. The form requires a
                  breakdown of each transaction, including the description of
                  the asset, date of acquisition, date of sale, sales price, and
                  cost or other basis.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  These assets can include stocks, bonds, real estate, and other
                  investment holdings. The schedule separates transactions into
                  short-term (held for one year or less) and long-term (held for
                  more than one year) categories, ultimately calculating the net
                  capital gain or loss for each.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Embark on a Smooth Schedule D Filing Journey with
                        TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Complete your Schedule D filing requirements with ease!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 140, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1041schd`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file Schedule D (Form 1041)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Any estate or trust</span>{' '}
                  that has realized capital gains or losses from the sale or
                  exchange of capital assets during the tax year must file
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule D (Form 1041)
                  </span>{' '}
                  along with{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990-T return
                  </Link>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This includes:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Estates with income from the{' '}
                    <span style={{ fontWeight: 600 }}>
                      sale or exchange of capital assets
                    </span>
                    .
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Trusts that have realized{' '}
                    <span style={{ fontWeight: 600 }}>
                      capital gains or losses from investments
                    </span>
                    .
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Any estate or trust with{' '}
                    <span style={{ fontWeight: 600 }}>
                      capital gain distributions
                    </span>{' '}
                    from mutual funds or other regulated investment companies.
                  </li>
                </ul>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="choose"
                >
                  Schedule D (Form 1041) filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schd-f1041-img.webp" />
                  <source srcSet="/assets/images/schd-f1041-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schd-f1041-img.png"
                    loading="lazy"
                    alt="Schedule D (Form 1041) filing Requirements"
                    width={760}
                    height={157}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This Schedule D (Form 1041) is used to report and calculate
                  the capital gains and losses of an estate or trust from the
                  sale or exchange of capital assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Name of estate or trust
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the name of the estate or trust filing the return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Employer Identification Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter its Employer Identification Number (EIN)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Indicate Yes or No if the estate or trust sold or disposed
                    of any investment in a Qualified Opportunity Fund during the
                    tax year.
                  </li>
                  <li>
                    If “Yes,” attach Form 8949 and follow its instructions to
                    properly report the capital gain or loss from that
                    transaction.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Below, we have provided Schedule D (Form 1041) filing
                  requirements for each part.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I-Short-Term Capital Gains and Losses Generally Assets
                  held 1 year or less
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schd-f1041-part1-img.webp" />
                  <source srcSet="/assets/images/schd-f1041-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schd-f1041-part1-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule D Part I showing short-term capital gains and losses for assets held one year or less, including sales, cost basis, and adjustments."
                    width={760}
                    height={377}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This is the total amount you received when you sold the asset.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This is what you originally paid for the asset, including
                  certain fees.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.3 }}>
                  This includes any corrections or special adjustments from{' '}
                  <Link
                    href="/resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8949
                  </Link>{' '}
                  that change your gain or loss amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For example, wash sales or other IRS-required changes.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This is your final result
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Formula:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Column(d)-Column(e) = result
                </Typography>
                <Typography variant="body1">
                  Combine the result with Column(g)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1a
                </Typography>
                <Typography variant="body1">
                  Enter totals for short-term transactions reported on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 1099-B or 1099-DA
                  </span>{' '}
                  where:
                </Typography>

                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    The IRS already received your cost basis, AND
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    You have no adjustments to make.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you report everything on Form 8949 instead, leave this line
                  blank.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter totals from{' '}
                  <span style={{ fontWeight: 600 }}>Form 8949</span> where Box A
                  or Box G is checked. These usually involve transactions where
                  basis was reported to the IRS, but adjustments may apply.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter totals from{' '}
                  <span style={{ fontWeight: 600 }}>Form 8949</span> where Box B
                  or Box H is checked. These are usually transactions where the
                  cost basis was{' '}
                  <span style={{ fontWeight: 600 }}>not reported</span> to the
                  IRS.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter totals from{' '}
                  <span style={{ fontWeight: 600 }}>Form 8949</span> where Box C
                  or Box I is checked. These are transactions that were{' '}
                  <span style={{ fontWeight: 600 }}>
                    not reported on Form 1099-B
                  </span>
                  .{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Short-term gain from{' '}
                  <span style={{ fontWeight: 600 }}>installment sales</span>{' '}
                  (from Form 6252, 4684, 6781, 8824). If you’re receiving
                  payments over time from a sale, the short-term portion goes
                  here.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is used to report the total short-term capital gain
                  or loss the estate or trust received from other entities, such
                  as partnerships, S corporations, or another estate or
                  trust.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any unused short-term capital loss from the previous
                  year. This amount comes from line 9 of the 2024 Capital Loss
                  Carryover Worksheet.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Add everything from lines 1a through 6 and enter in column
                  h.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II-Long-term Capital Gains and Losses-Generally Assets
                  held more than 1 year
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schd-f1041-part2-img.webp" />
                  <source srcSet="/assets/images/schd-f1041-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schd-f1041-part2-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule D showing capital gains and losses, including short-term and long-term asset sales, cost basis, adjustments, and net gain or loss."
                    width={760}
                    height={399}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The columns(d-h) in Part II are the same as the columns
                  explained earlier in Part I, they follow the same meaning and
                  calculation method described above.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this line to report the total of long-term sales shown on
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1099-B
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-da/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1099-DA
                  </Link>{' '}
                  when the cost basis was already reported to the IRS and no
                  adjustments are needed. Instead of listing each transaction on
                  Form 8949, the corporation can combine them and report the
                  totals directly here.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is used to transfer the total amounts from Form 8949
                  for long-term transactions where Box D or Box J is checked.
                  These transactions were already detailed on Form 8949, so here
                  you simply enter the combined totals.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total of long-term transactions from Form 8949 where
                  Box E or Box K is checked. These usually represent
                  transactions where the cost basis was not reported to the IRS,
                  so they must first be reported on Form 8949 before being
                  summarized here.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this line to report the totals from Form 8949 for
                  transactions with Box F or Box L checked. These are long-term
                  sales that were not reported on a Form 1099, so they must be
                  listed on Form 8949 and then summarized on this line.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any long-term gain or loss reported on Forms 2439, 4684,
                  6252, 6781, or 8824 from certain transactions during the year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the long-term capital gain or loss received from
                  partnerships, S corporations, or other estates or trusts. This
                  amount usually comes from Schedule K-1.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the estate or trust received any capital gain payouts from
                  investments, such as mutual funds or similar investment funds,
                  report the total amount on this line.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If there was a profit from selling certain business property,
                  the gain reported on{' '}
                  <Link
                    href="/resources/tax-exempt/form-4797-sales-business-property/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 4797
                  </Link>
                  , Part I should be entered here.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If some long-term capital losses from last year couldn’t be
                  used, the remaining amount that was carried forward should be
                  entered on this line.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Add together the amounts from lines 8a through 15 to find the
                  total long-term capital gain or loss for the year and report
                  the result here.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III-Summary of Parts I and II
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schd-f1041-part3-img.webp" />
                  <source srcSet="/assets/images/schd-f1041-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schd-f1041-part3-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule D Part III summarizing short-term and long-term capital gains and losses from Parts I and II, showing total net gain or loss."
                    width={760}
                    height={178}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (1)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The portion of the gain/loss that is passed through to the
                  heirs.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (2)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The portion of the estate or trust kept will pay taxes
                  directly.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (3)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The sum of both. This is the total performance for the year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The result of all assets held for one year or less. This is
                  usually taxed at higher, ordinary income rates.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 18a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The final result of all assets held for more than a year.
                  These usually get a break with lower tax rates.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 18b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A specific tax on profit from selling real estate that was
                  previously depreciated.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 18c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Profits from collectibles (like art, coins, or antiques) or
                  specific small business stock. These are taxed at a flat
                  28%.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 19
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The Bottom Line. You add your short-term and long-term results
                  together here to see if you made or lost money overall
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV-Capital Loss Limitation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schd-f1041-part4-img.webp" />
                  <source srcSet="/assets/images/schd-f1041-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schd-f1041-part4-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule D Part IV showing the capital loss limitation, including deduction limits, carryovers, and amounts applied against gains."
                    width={760}
                    height={97}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If a trust files Schedule D with Form 990-T and has more than
                  one unrelated trade or business, it must calculate the taxable
                  income for each business separately. The amount for each
                  business should then be reported on line 4c of Part I of{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-t-schedule-a/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule A (Form 990-T)
                  </Link>{' '}
                  for that specific activity.{' '}
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong>If the loss
                  on line 19, column (3) is more than $3,000, or if Form 1041
                  (page 1, line 23) or Form 990-T (Part I, line 11) shows a
                  loss, complete the Capital Loss Carryover Worksheet in the
                  instructions to calculate the capital loss carryover.
                </Alert>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution: </strong>It is
                  important to remember that each unrelated trade or business
                  must be reported separately, rather than combining them into
                  one total.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part V-Tax Computation using Maximum Capital Gains Rates
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schd-f1041-part5-img.webp" />
                  <source srcSet="/assets/images/schd-f1041-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schd-f1041-part5-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule D Part V calculating tax using maximum capital gains rates, applying different rates to short-term and long-term gains."
                    width={760}
                    height={383}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 21
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the taxable income of the estate or trust from Form 1041
                  (line 23). This is the starting point for the tax
                  calculation.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller amount between line 18a and line 19 (column
                  2), but it cannot be less than zero. This helps determine the
                  portion of income related to capital gains.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 23
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the qualified dividends received by the estate or trust
                  from Form 1041 (line 2b(2)). These dividends may be taxed at
                  special capital gain rates.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 24
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add line 22 and line 23. This gives the total amount of income
                  that may qualify for the special tax rates.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 25
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the estate or trust is filing Form 4952, enter the amount
                  from line 4g of that form. Otherwise, enter 0.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 26
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Subtract line 25 from line 24. If the result is negative,
                  enter 0.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 27
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Subtract line 26 from line 21. If the result is negative,
                  enter 0.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 28
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the smaller amount between line 21 or $3,250.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 29
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the smaller amount between line 27 or line 28.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 30
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 29 from line 28. If the result is zero or less,
                  enter 0.This amount represents the portion of income taxed at
                  the 0% capital gains rate.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schd-f1041-part5-line31-img.webp" />
                  <source srcSet="/assets/images/schd-f1041-part5-line31-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schd-f1041-part5-line31-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule D Part V calculating tax using maximum capital gains rates, applying different rates to short-term and long-term gains."
                    width={760}
                    height={285}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 31
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Compare the numbers on line 21 and line 26. Take whichever is
                  smaller and write it here.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 32
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Take the number on line 26 and see what’s left after taking
                  away line 30. Think of it like what’s remaining after you’ve
                  already used some of it
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 33
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Compare line 21 with $15,900. Write the smaller amount. It’s
                  basically setting a cap at $15,900.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 34
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Simply combine the amounts from lines 27 and 30. This gives
                  you the total of these two parts together.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 35
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract line 34 from line 33. If zero or less, enter 0
                  </li>
                  <li>
                    Take line 33 and subtract what you got in line 34. If the
                    result is negative or zero, just write 0. This ensures you
                    don’t carry a negative amount forward.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 36
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter the smaller of line 32 or line 35. </li>
                  <li>
                    Look at the numbers in lines 32 and 35. Pick the smaller
                    number. This limits the next calculation to the lower of the
                    two amounts.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 37
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Multiply line 36 by 15% (0.15).</li>
                  <li>
                    Take the number from line 36 and calculate 15% of it. This
                    gives one portion of the tax you owe.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 38
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter the amount from line 31. </li>
                  <li>
                    Just copy the number you got in line 31. No calculation
                    here.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 39
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Add lines 30 and 36. </li>
                  <li>
                    Add together the amounts from lines 30 and 36. This is
                    another subtotal used for the next step.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 40
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract line 39 from line 38. If zero or less, enter
                    0.{' '}
                  </li>
                  <li>
                    Take line 38 and subtract line 39. If the result is negative
                    or zero, write 0. Again, this avoids negative numbers in tax
                    calculation.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 41
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Take the result from line 40 and calculate 20% of it. This is
                  another piece of the tax calculation.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 42
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use the 2025 Tax Rate Schedule for Estates and Trusts to
                  calculate the tax owed on the amount from line 27. This
                  follows official IRS tax tables.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 43
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add up the three calculated amounts from lines 37, 41, and 42.
                  This gives a combined total tax based on the special
                  calculations above.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 44
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use the 2025 Tax Rate Schedule for Estates and Trusts again
                  but now calculate the tax on the number from line 21.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 45
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Compare line 43 and line 44. Take the smaller number. This
                  ensures you’re paying the correct, lower tax and then put it
                  on Form 1041, Schedule G, line 1a (or Form 990-T, Part II,
                  line 2).
                </Typography>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 2,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                    Streamline Your 990-T Filing with TaxZerone!
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Make your e-filing process simple by clicking the button
                    below.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1041schd`}
                  >
                    E-File Form 990-T
                  </Button>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i1041sd.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule D (Form 1041)
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i1041.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 1041
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990t.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990-T
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/publications/p559"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Publication
                          </Link>{' '}
                          559
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What information do I need to complete Schedule D?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    To complete Schedule D (Form 1041), you&apos;ll need details
                    about the capital assets sold or exchanged, including the
                    type of asset, date of acquisition, date of sale, sales
                    price, and basis (original cost) of the asset for the estate
                    or trust.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Can estates and trusts offset capital gains with capital
                  losses?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      Yes, estates and trusts can offset capital gains with
                      capital losses. If capital losses exceed capital gains,
                      the excess loss can be carried forward to future tax
                      years.
                    </Typography>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                    >
                      <strong style={{ fontWeight: 600 }}>Note:</strong> Unlike
                      individuals, estates and trusts cannot deduct any excess
                      capital losses from their ordinary income (income from
                      wages, interest, etc.).
                    </Alert>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What happens if Schedule D (Form 1041) is not filed when
                  required?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Failure to file Schedule D (Form 1041) when required can
                    result in penalties and interest charges from the IRS.
                    It&apos;s important to accurately report all capital gains
                    and losses to avoid these penalties.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form1041SchD;
